import { useEffect, useState } from "react";
import { api, apiErr } from "@/admin/api";
import { PageTitle, fmtDate } from "@/admin/ui";
import { useAuth } from "@/admin/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, KeyRound, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { v: "super_admin", l: "Super Admin" },
  { v: "content_admin", l: "Content Admin" },
  { v: "inquiry_manager", l: "Inquiry Manager" },
];

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "inquiry_manager" });
  const [saving, setSaving] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/auth/users"); setUsers(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || form.password.length < 6) { toast.error("Name, email and a 6+ char password are required."); return; }
    setSaving(true);
    try { await api.post("/auth/users", form); toast.success("User created"); setOpen(false); setForm({ name: "", email: "", password: "", role: "inquiry_manager" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/auth/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const changeRole = async (u, role) => {
    try { await api.patch(`/auth/users/${u.id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const resetPw = async () => {
    if (newPw.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    try { await api.post(`/auth/users/${pwUser.id}/reset-password`, { new_password: newPw }); toast.success("Password reset"); setPwUser(null); setNewPw(""); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/auth/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="admin-users">
      <PageTitle title="Admin Users" subtitle="Manage who can access the admin console">
        <Button data-testid="new-user" onClick={() => setOpen(true)} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">
          <Plus size={16} /> Add User
        </Button>
      </PageTitle>

      <div className="bg-white rounded-2xl border border-bone overflow-hidden">
        {!users ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-terracotta" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-sand text-ink-soft text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Active</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Added</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bone">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-ink">{u.name}</p>
                      <p className="text-xs text-ink-soft">{u.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === me.id}>
                        <SelectTrigger className="rounded-lg border-bone h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)} disabled={u.id === me.id} />
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-ink-soft">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button onClick={() => setPwUser(u)} variant="ghost" size="icon" className="rounded-lg text-ink-soft" title="Reset password"><KeyRound size={16} /></Button>
                      {u.id !== me.id && <Button onClick={() => remove(u)} variant="ghost" size="icon" className="rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={16} /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-sand">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-ink">Add Admin User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col gap-1.5"><Label>Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-bone bg-white" /></div>
            <div className="flex flex-col gap-1.5"><Label>Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border-bone bg-white" /></div>
            <div className="flex flex-col gap-1.5"><Label>Temporary Password</Label><Input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl border-bone bg-white" /></div>
            <div className="flex flex-col gap-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role" className="rounded-xl border-bone bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="user-create" onClick={create} disabled={saving} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white">
              {saving ? <Loader2 className="animate-spin" size={16} /> : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent className="bg-sand">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-ink">Reset Password</DialogTitle></DialogHeader>
          <p className="text-sm text-ink-soft">Set a new password for <span className="font-medium text-ink">{pwUser?.email}</span></p>
          <Input data-testid="reset-password-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password" className="rounded-xl border-bone bg-white" />
          <DialogFooter>
            <Button data-testid="reset-password-submit" onClick={resetPw} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
